import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { logEvent } from '../utils/analytics';
import './QuestionBankPractice.css';
import './Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function QuestionBankPractice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookId } = useParams();
  const [userData, setUserData] = useState(null);
  const [activeMenu, setActiveMenu] = useState('question-bank');

  // Filter states
  const [selectedFilter, setSelectedFilter] = useState('unattempted'); // all, unattempted, incorrect, bookmarked, revision_due
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [topics, setTopics] = useState([]);
  const [showTopicMenu, setShowTopicMenu] = useState(false);

  // Question states
  const [questions, setQuestions] = useState([]);
  const [totalAvailableQuestions, setTotalAvailableQuestions] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [explanation, setExplanation] = useState('');
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [bookInfo, setBookInfo] = useState(null);
  const [notes, setNotes] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);

  const filterOptions = [
    { id: 'all', label: 'All Questions' },
    { id: 'unattempted', label: 'Unattempted' },
    { id: 'incorrect', label: 'Previously Incorrect' },
    { id: 'bookmarked', label: 'Bookmarked' },
    { id: 'revision_due', label: 'Due for Revision' }
  ];

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (userData && bookId) {
      loadBookInfo();
      loadTopics();
    }
  }, [userData, bookId]);

  useEffect(() => {
    if (userData) {
      loadQuestions();
    }
  }, [userData, bookId, selectedFilter, selectedTopic]);

  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex < questions.length) {
      setCurrentQuestion(questions[currentQuestionIndex]);
      setIsAnswerSubmitted(false);
      setSelectedOption(null);
      setIsCorrect(false);
      setCorrectAnswer('');
      setExplanation('');
      setQuestionStartTime(Date.now());
      checkBookmarkStatus(questions[currentQuestionIndex].id);
    }
  }, [currentQuestionIndex, questions]);

  // Handle navigation from bookmarks with specific question_id
  useEffect(() => {
    if (questions.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const questionId = searchParams.get('question_id');

      if (questionId) {
        // Find the index of the question with this ID
        const questionIndex = questions.findIndex(q => q.id === parseInt(questionId));

        if (questionIndex !== -1) {
          console.log(`Found question ${questionId} at index ${questionIndex}`);
          setCurrentQuestionIndex(questionIndex);

          // Remove question_id from URL to prevent re-triggering
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        } else {
          console.warn(`Question ${questionId} not found in current question set`);
        }
      }
    }
  }, [questions, location.search]);

  const checkSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated && data.data && data.data.doctor) {
        setUserData(data.data.doctor);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      navigate('/login');
    }
  };

  const loadBookInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/books/${bookId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.data) {
        setBookInfo(data.data);
      }
    } catch (error) {
      console.error('Error loading book info:', error);
    }
  };

  const loadQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      // Load filtered questions
      let url = `${API_BASE_URL}/question-bank/practice/questions?filter=${selectedFilter}&limit=50`;
      if (bookId) {
        url += `&book_id=${bookId}`;
      }
      if (selectedTopic && selectedTopic !== 'all') {
        url += `&topic=${encodeURIComponent(selectedTopic)}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      if (data.questions) {
        setQuestions(data.questions);
        setCurrentQuestionIndex(0);
      }
      // Set total from the response (this is total for current filter)
      if (data.total !== undefined) {
        // Only update total available if filter is "all"
        if (selectedFilter === 'all') {
          setTotalAvailableQuestions(data.total);
        }
      }

      // If filter is NOT "all", fetch total available count separately
      if (selectedFilter !== 'all') {
        let totalUrl = `${API_BASE_URL}/question-bank/practice/questions?filter=all&limit=1`;
        if (bookId) {
          totalUrl += `&book_id=${bookId}`;
        }
        const totalResponse = await fetch(totalUrl, { credentials: 'include' });
        const totalData = await totalResponse.json();
        if (totalData.total !== undefined) {
          setTotalAvailableQuestions(totalData.total);
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const loadTopics = async () => {
    try {
      let url = `${API_BASE_URL}/question-bank/practice/topics`;
      if (bookId) {
        url += `?book_id=${bookId}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      if (data.topics) {
        setTopics(data.topics);
      }
    } catch (error) {
      console.error('Error loading topics:', error);
    }
  };

  const checkBookmarkStatus = async (questionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/question-bank/practice/bookmarks`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.bookmarks) {
        const bookmark = data.bookmarks.find(b => b.question_id === questionId);
        setIsBookmarked(!!bookmark);
        setNotes(bookmark?.notes || '');
      }
    } catch (error) {
      console.error('Error checking bookmark:', error);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!currentQuestion || !selectedOption) {
      alert('Please select an answer');
      return;
    }

    const timeTaken = questionStartTime ? Math.round((Date.now() - questionStartTime) / 1000) : null;

    setIsSubmittingAnswer(true);

    try {
      const response = await fetch(`${API_BASE_URL}/question-bank/practice/attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_id: currentQuestion.id,
          selected_answer: selectedOption,
          time_taken: timeTaken
        })
      });

      const data = await response.json();
      if (response.ok) {
        setIsCorrect(data.is_correct);
        setCorrectAnswer(data.correct_answer);
        setExplanation(data.explanation);
        setIsAnswerSubmitted(true);

        logEvent('Question Bank', 'question_answered', {
          question_id: currentQuestion.id,
          book_id: bookId,
          is_correct: data.is_correct,
          time_taken: timeTaken
        });
      } else {
        alert('Failed to submit answer: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('Failed to submit answer. Please try again.');
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const handleNextQuestion = () => {
    // If this question was answered and filter is 'unattempted', remove it from the list
    if (isAnswerSubmitted && selectedFilter === 'unattempted') {
      const updatedQuestions = questions.filter((_, index) => index !== currentQuestionIndex);
      setQuestions(updatedQuestions);

      // Check if there are more questions
      if (updatedQuestions.length === 0) {
        alert('Great job! You have completed all unattempted questions!');
        navigate('/question-bank');
      } else if (currentQuestionIndex >= updatedQuestions.length) {
        // We were at the last question, go to the new last question
        setCurrentQuestionIndex(updatedQuestions.length - 1);
      }
      // else: stay at same index (which now points to next question due to removal)
    } else {
      // Normal navigation
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Reached end of questions
        alert('You have completed all questions in this set!');
        navigate('/question-bank');
      }
    }
  };

  const handleBookmark = async () => {
    if (!currentQuestion) return;

    try {
      if (isBookmarked) {
        // Remove bookmark
        const response = await fetch(
          `${API_BASE_URL}/question-bank/practice/bookmark/${currentQuestion.id}`,
          {
            method: 'DELETE',
            credentials: 'include'
          }
        );
        const data = await response.json();
        if (data.success) {
          setIsBookmarked(false);
          setNotes('');
          setShowNotesInput(false);
        }
      } else {
        // Add bookmark
        const response = await fetch(`${API_BASE_URL}/question-bank/practice/bookmark`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            question_id: currentQuestion.id,
            notes: notes
          })
        });
        const data = await response.json();
        if (data.success) {
          setIsBookmarked(true);
        }
      }
    } catch (error) {
      console.error('Error bookmarking:', error);
    }
  };

  const handleSaveNotes = async () => {
    if (!currentQuestion) return;

    try {
      const response = await fetch(`${API_BASE_URL}/question-bank/practice/bookmark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_id: currentQuestion.id,
          notes: notes
        })
      });
      const data = await response.json();
      if (data.success) {
        setIsBookmarked(true);
        setShowNotesInput(false);
        alert('Notes saved!');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem);
    if (menuItem === 'home') {
      navigate('/dashboard');
    } else if (menuItem === 'ai-chat') {
      navigate('/chat');
    } else if (menuItem === 'exams') {
      navigate('/exams');
    } else if (menuItem === 'question-bank') {
      navigate('/question-bank');
    } else if (menuItem === 'feedback') {
      navigate('/feedback');
    } else if (menuItem === 'profile') {
      navigate('/edit-profile');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getMenuItems = () => {
    if (!userData) return [];

    if (userData.user_type === 'medical_student') {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'exams', label: 'Exams', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
        { id: 'question-bank', label: 'Question Bank', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    } else {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    }
  };

  const menuItems = getMenuItems().filter(item => item.id !== 'profile');

  return (
    <div className="question-bank-practice">
      {/* Top Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="dashboard-logo-img" />
        </div>
        <nav className="dashboard-nav desktop-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="nav-menu-icon">{item.icon}</span>
              <span className="nav-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="dashboard-logout-btn">
          <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
            <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <span className="logout-text">Logout</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="practice-container">
        <div className="practice-header">
          <button className="practice-back-btn" onClick={() => navigate('/question-bank')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div className="practice-title-section">
            <h1 className="practice-title">{bookInfo?.name || 'Question Bank'}</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="filter-btn-wrapper">
              <button className="filter-btn" onClick={() => setShowFilterMenu(!showFilterMenu)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" strokeWidth="1.4">
                  <path d="M4 6H20M7 12H17M10 18H14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {filterOptions.find(f => f.id === selectedFilter)?.label}
              </button>
              {showFilterMenu && (
                <div className="filter-menu">
                  {filterOptions.map(filter => (
                    <button
                      key={filter.id}
                      className={`filter-option ${selectedFilter === filter.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedFilter(filter.id);
                        setShowFilterMenu(false);
                      }}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {topics.length > 0 && (
              <div className="filter-btn-wrapper">
                <button className="filter-btn" onClick={() => setShowTopicMenu(!showTopicMenu)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 7h.01M7 12h.01M7 17h.01M12 7h10M12 12h10M12 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {selectedTopic === 'all' ? 'All Topics' : selectedTopic}
                </button>
                {showTopicMenu && (
                  <div className="filter-menu">
                    <button
                      className={`filter-option ${selectedTopic === 'all' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedTopic('all');
                        setShowTopicMenu(false);
                      }}
                    >
                      All Topics
                    </button>
                    {topics.map(topic => (
                      <button
                        key={topic}
                        className={`filter-option ${selectedTopic === topic ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedTopic(topic);
                          setShowTopicMenu(false);
                        }}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isLoadingQuestions ? (
          <div className="loading-state">
            <img src="/logo.svg" alt="Loading" className="loading-logo" />
            <p>Loading questions...</p>
          </div>
        ) : !currentQuestion ? (
          <div className="empty-state">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#9ca3af">
              <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3.89543 5 3 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>No questions found</h3>
            <p>Try selecting a different filter</p>
          </div>
        ) : (
          <div className="question-card">
            {/* Question Text */}
            <div className="question-content">
              <h3 className="question-text">Q. {currentQuestion.question_text}</h3>

              {/* MCQ Options */}
              <div className="question-options">
                {['option_a', 'option_b', 'option_c', 'option_d', 'option_e'].map((optKey) => {
                  const optValue = currentQuestion[optKey];
                  if (!optValue) return null;

                  const optionLetter = optKey.split('_')[1].toUpperCase();

                  return (
                    <label
                      key={optKey}
                      className={`question-option ${selectedOption === optionLetter ? 'selected' : ''} ${isAnswerSubmitted && optionLetter === correctAnswer ? 'correct' : ''} ${isAnswerSubmitted && selectedOption === optionLetter && optionLetter !== correctAnswer ? 'incorrect' : ''}`}
                    >
                      <input
                        type="radio"
                        name="question-option"
                        value={optionLetter}
                        checked={selectedOption === optionLetter}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        disabled={isAnswerSubmitted}
                        className="question-option-input"
                      />
                      <span className="question-option-label">
                        <strong>{optionLetter}.</strong> {optValue}
                      </span>
                      {isAnswerSubmitted && optionLetter === correctAnswer && (
                        <svg className="option-check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      {isAnswerSubmitted && selectedOption === optionLetter && optionLetter !== correctAnswer && (
                        <svg className="option-cross-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            {!isAnswerSubmitted && (
              <div className="question-actions">
                <button
                  className="submit-btn"
                  onClick={handleSubmitAnswer}
                  disabled={!selectedOption || isSubmittingAnswer}
                >
                  {isSubmittingAnswer ? 'Generating explanation...' : 'Submit Answer'}
                </button>
              </div>
            )}

            {/* Result Section */}
            {isAnswerSubmitted && (
              <div className="question-result">
                <div className={`result-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
                  {isCorrect ? (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Correct!</span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>Incorrect</span>
                    </>
                  )}
                </div>

                {explanation && (
                  <div className="question-explanation">
                    <h4 className="explanation-title">Explanation</h4>
                    <div className="explanation-text">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {explanation}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                <div className="result-actions">
                  <button
                    className={`bookmark-btn ${isBookmarked ? 'bookmarked' : ''}`}
                    onClick={() => setShowNotesInput(!showNotesInput)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill={isBookmarked ? "currentColor" : "none"} xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 21L12 16L5 21V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                  </button>
                  {showNotesInput && (
                    <div className="notes-input-wrapper">
                      <textarea
                        className="notes-input"
                        placeholder="Add notes (optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                      />
                      <div className="notes-actions">
                        <button className="save-notes-btn" onClick={handleSaveNotes}>
                          Save Notes
                        </button>
                        <button className="cancel-notes-btn" onClick={() => setShowNotesInput(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <button className="next-btn" onClick={handleNextQuestion}>
                    Next Question
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Menu */}
      <nav className="dashboard-nav mobile-nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
            onClick={() => handleMenuClick(item.id)}
          >
            <span className="nav-menu-icon">{item.icon}</span>
            <span className="nav-menu-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default QuestionBankPractice;
